import { ProForm } from '@ant-design/pro-components'
import { InputNumber, type InputNumberProps } from 'antd'
import type React from 'react'
import type { FocusEvent } from 'react'
import { useEffect, useId, useRef } from 'react'
import { EditableLabel } from '../components/EditableLabel'
import { EditableShell } from '../components/EditableShell'
import { RenderErrors } from '../components/RenderErrors'
import { useFieldContext } from '../createAppFormHook'
import { useEditableFieldMode } from '../hooks/useEditableFieldMode'

type SpinnerStepInfo = {
  emitter?: 'handler' | 'keyboard' | 'wheel' | string
}

type InputNumberMode = {
  mode?: 'input' | 'spinner' | string
  onStep?: (value: number | string | null, info: SpinnerStepInfo) => void
}

export type InputNumberFieldProps = InputNumberProps<number> &
  InputNumberMode & {
    label?: React.ReactNode
    className?: string
    formItemProps?: React.ComponentProps<typeof ProForm.Item>
    fieldProps?: Pick<InputNumberProps<number>, 'id'>
    readonly?: boolean
  }

const SPINNER_CONTROL_SELECTOR =
  '.ant-input-number-handler-wrap, .ant-input-number-handler, .ant-input-number-handler-up, .ant-input-number-handler-down, .ant-input-number-action, .ant-input-number-action-up, .ant-input-number-action-down'
const NUMBER_INPUT_SELECTOR = '.ant-input-number-input'
const NUMBER_INPUT_INTERACTIVE_SELECTOR = '.ant-input-number-input-wrap, .ant-input-number-input'

type LastTouchInteraction = 'spinner' | 'input' | 'other'

function toDisplayValue(
  value: number | string | null | undefined,
  decimalSeparator?: string,
  precision?: number,
): string {
  if (value == null || value === '') {
    return '-'
  }

  const numericValue = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numericValue)) {
    return String(value)
  }

  const options: Intl.NumberFormatOptions =
    typeof precision === 'number'
      ? {
          minimumFractionDigits: precision,
          maximumFractionDigits: precision,
        }
      : {}

  let text = new Intl.NumberFormat(undefined, options).format(numericValue)

  if (decimalSeparator) {
    const localeDecimalSeparator = new Intl.NumberFormat(undefined)
      .formatToParts(1.1)
      .find(part => part.type === 'decimal')?.value

    if (localeDecimalSeparator && localeDecimalSeparator !== decimalSeparator) {
      text = text.replace(localeDecimalSeparator, decimalSeparator)
    }
  }

  return text
}

export function InputNumberField(props: InputNumberFieldProps) {
  const fieldCtx = useFieldContext<number | undefined>()
  const id = useId()
  const {
    className,
    label,
    formItemProps,
    fieldProps,
    placeholder,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    onCompositionStart,
    onCompositionEnd,
    onStep,
    mode,
    suffix,
    decimalSeparator,
    readonly: _readonly,
    ...rest
  } = props

  const editable = useEditableFieldMode<number | undefined>()
  const errors = fieldCtx.state.meta.errors
  const fieldValue = (props.value !== undefined ? props.value : fieldCtx.state.value) as number | null | undefined
  const hasOwnChangeHandler = typeof onChange === 'function'
  const lastTouchInteractionRef = useRef<LastTouchInteraction>('other')
  const shouldSelectOnNextFocusRef = useRef(false)
  const pendingBlurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const isSpinnerMode = mode === 'spinner'
  const controlId = rest.id ?? fieldProps?.id ?? id

  const clearPendingBlur = () => {
    if (pendingBlurTimeoutRef.current) {
      clearTimeout(pendingBlurTimeoutRef.current)
      pendingBlurTimeoutRef.current = null
    }
  }

  const blurActiveNumberInput = () => {
    const activeElement = document.activeElement
    if (!(activeElement instanceof HTMLInputElement)) {
      return
    }

    if (!activeElement.matches(NUMBER_INPUT_SELECTOR)) {
      return
    }

    if (!wrapperRef.current?.contains(activeElement)) {
      return
    }

    activeElement.blur()
  }

  useEffect(() => {
    return () => {
      if (pendingBlurTimeoutRef.current) {
        clearTimeout(pendingBlurTimeoutRef.current)
        pendingBlurTimeoutRef.current = null
      }
    }
  }, [])

  const displayValue = toDisplayValue(fieldValue, decimalSeparator, props.precision)

  return (
    <EditableShell
      mode={editable.mode}
      enterEdit={editable.enterEdit}
      exitRead={editable.exitRead}
      className={className}
    >
      <ProForm.Item
        {...formItemProps}
        htmlFor={controlId}
        required={formItemProps?.required ?? props.required}
        label={
          <EditableLabel
            mode={editable.mode}
            enterEdit={editable.enterEdit}
            exitRead={editable.exitRead}
            onUndo={editable.undoAndExit}
            canUndo={editable.canUndo}
            showActions={!props.readonly}
          >
            {label}
          </EditableLabel>
        }
        validateStatus={formItemProps?.validateStatus ?? (errors.length > 0 ? 'error' : undefined)}
        help={formItemProps?.help ?? <RenderErrors errors={errors} />}
      >
        {editable.mode === 'read' ? (
          <span>
            {displayValue}
            {displayValue !== '-' && suffix ? <> {suffix}</> : null}
          </span>
        ) : (
          <div
            ref={wrapperRef}
            onPointerDownCapture={event => {
              if (!isSpinnerMode) {
                return
              }

              clearPendingBlur()

              const target = event.target as HTMLElement | null
              const isSpinnerControl = Boolean(target?.closest(SPINNER_CONTROL_SELECTOR))
              const isTouchLikePointer = event.pointerType === 'touch' || event.pointerType === 'pen'
              const isInputArea = Boolean(target?.closest(NUMBER_INPUT_INTERACTIVE_SELECTOR))

              if (isSpinnerControl) {
                lastTouchInteractionRef.current = isTouchLikePointer ? 'spinner' : 'other'
                shouldSelectOnNextFocusRef.current = false
                return
              }

              if (isInputArea) {
                lastTouchInteractionRef.current = isTouchLikePointer ? 'input' : 'other'
                shouldSelectOnNextFocusRef.current = true
                return
              }

              lastTouchInteractionRef.current = 'other'
              shouldSelectOnNextFocusRef.current = false
            }}
          >
            <InputNumber
              {...rest}
              id={controlId}
              mode={mode}
              suffix={suffix}
              decimalSeparator={decimalSeparator}
              value={fieldValue ?? null}
              placeholder={placeholder ?? (typeof label === 'string' ? label : undefined)}
              onChange={value => {
                onChange?.(value)

                if (!hasOwnChangeHandler) {
                  fieldCtx.handleChange(value == null ? undefined : value)
                }
              }}
              onBlur={event => {
                onBlur?.(event)
                editable.onBlur()
              }}
              onKeyDown={event => {
                onKeyDown?.(event)
                editable.onKeyDown(event)
              }}
              onCompositionStart={event => {
                onCompositionStart?.(event)
                editable.onCompositionStart?.()
              }}
              onCompositionEnd={event => {
                onCompositionEnd?.(event)
                editable.onCompositionEnd?.()
              }}
              onStep={(value, info) => {
                onStep?.(value, info as SpinnerStepInfo)

                if ((info as SpinnerStepInfo).emitter !== 'handler') {
                  return
                }

                if (lastTouchInteractionRef.current !== 'spinner') {
                  return
                }

                requestAnimationFrame(() => {
                  if (lastTouchInteractionRef.current === 'spinner') {
                    blurActiveNumberInput()
                  }
                })

                clearPendingBlur()
                pendingBlurTimeoutRef.current = setTimeout(() => {
                  if (lastTouchInteractionRef.current === 'spinner') {
                    blurActiveNumberInput()
                  }
                }, 80)
              }}
              onFocus={(event: FocusEvent<HTMLInputElement>) => {
                onFocus?.(event)

                if (!shouldSelectOnNextFocusRef.current) {
                  return
                }

                shouldSelectOnNextFocusRef.current = false
                const input =
                  event.target instanceof HTMLInputElement
                    ? event.target
                    : wrapperRef.current?.querySelector<HTMLInputElement>(NUMBER_INPUT_SELECTOR)
                input?.select()
              }}
            />
          </div>
        )}
      </ProForm.Item>
    </EditableShell>
  )
}
